import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Journey,
  JourneyPhase,
  JourneyPhaseView,
  JourneyProgress,
  JourneyStep,
  JourneyTrack,
} from '../models/journey.models';

@Injectable({
  providedIn: 'root',
})
export class JourneyService {
  private http = inject(HttpClient);
  private readonly url = `${environment.apiUrl}/journey`;

  private _journey = signal<Journey | null>(null);
  readonly journey = this._journey.asReadonly();

  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  /** Every step, in catalogue order — the shape the API sends. */
  readonly steps = computed<JourneyStep[]>(() => this._journey()?.steps ?? []);

  /** The two tracks are counted separately on purpose: they have different lifetimes. */
  readonly userProgress = computed(() => countTrack(this.steps(), 'user'));
  readonly companyProgress = computed(() =>
    countTrack(this.steps(), 'company'),
  );

  /**
   * The same steps regrouped by phase, in order of first appearance, with the counts the
   * milestone path and the accordion draw. `isCurrent` marks the first unfinished phase — so
   * a phase left behind stays reachable without stealing the highlight.
   */
  readonly phases = computed<JourneyPhaseView[]>(() => {
    const groups: { phase: JourneyPhase; steps: JourneyStep[] }[] = [];

    for (const step of this.steps()) {
      const group = groups.find((candidate) => candidate.phase === step.phase);

      if (group) {
        group.steps.push(step);
      } else {
        groups.push({ phase: step.phase, steps: [step] });
      }
    }

    const currentIndex = groups.findIndex((group) =>
      group.steps.some((step) => !step.completed),
    );

    return groups.map((group, index) => {
      const completed = group.steps.filter((step) => step.completed).length;

      return {
        ...group,
        position: index + 1,
        completed,
        total: group.steps.length,
        isComplete: completed === group.steps.length,
        isCurrent: index === currentIndex,
      };
    });
  });

  readonly completedCount = computed(
    () => this.steps().filter((step) => step.completed).length,
  );
  readonly totalCount = computed(() => this.steps().length);

  /** True once every step of both tracks is ticked. Loading counts as "not finished". */
  readonly isComplete = computed(
    () => this.totalCount() > 0 && this.completedCount() === this.totalCount(),
  );

  /** First step left to do, in catalogue order. Drives the "next action" prompt. */
  readonly nextStep = computed<JourneyStep | null>(
    () => this.steps().find((step) => !step.completed) ?? null,
  );

  /** Share of the whole journey done, both tracks pooled. Drives the progress ring. */
  readonly overallPercent = computed(() =>
    this.totalCount() === 0
      ? 0
      : Math.round((this.completedCount() / this.totalCount()) * 100),
  );

  /** One badge per completed phase, so level and badge count are the same number. */
  readonly level = computed(
    () => this.phases().filter((phase) => phase.isComplete).length,
  );

  /** Level 0 is the starting point, level N the title earned with the Nth badge. */
  readonly levelTitleKey = computed(
    () => `help.onboarding.journey.levels.${this.level()}`,
  );

  /**
   * Whether the onboarding popup may open at all ("ne plus montrer"). False until the journey
   * is loaded: no journey, no popup.
   */
  readonly showPopup = computed(() => this._journey()?.showPopup ?? false);

  /**
   * Loads the journey unless it is already there. Two components racing on an empty cache
   * fire two GETs, which is harmless — and much less surprising than handing the loser a
   * `null` journey it would have to guess about.
   */
  loadIfNeeded(): Observable<Journey | null> {
    const journey = this._journey();

    return journey !== null ? of(journey) : this.load();
  }

  load(): Observable<Journey | null> {
    this.isLoading.set(true);
    this.error.set(null);

    return this.http.get<Journey>(this.url).pipe(
      tap((journey) => {
        this._journey.set(journey);
        this.isLoading.set(false);
      }),
      catchError((err) => {
        this.isLoading.set(false);
        this.error.set(err.message ?? 'Failed to load the onboarding journey');
        console.error(err);
        return of(null);
      }),
    );
  }

  /** Ticks or un-ticks a step from the journey page. */
  toggleStep(key: string): Observable<void> {
    const step = this.steps().find((candidate) => candidate.key === key);

    if (!step) {
      return throwError(() => new Error(`Unknown journey step "${key}"`));
    }

    return this.setStep(key, !step.completed);
  }

  /**
   * Optimistic: the checkbox flips immediately and rolls back if the call fails, mirroring
   * ReductionActionsService.
   */
  private setStep(key: string, completed: boolean): Observable<void> {
    this.patchStep(key, completed);

    const request = completed
      ? this.http.post<void>(`${this.url}/steps/${key}`, {})
      : this.http.delete<void>(`${this.url}/steps/${key}`);

    return request.pipe(
      catchError((err) => {
        this.patchStep(key, !completed);
        console.error(err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * "Ne plus montrer" ticked on the onboarding popup. Optimistic like the steps, and
   * idempotent server-side, so re-ticking it costs nothing.
   */
  dismissPopup(): Observable<void> {
    this.patchPopup(false);

    return this.http.delete<void>(`${this.url}/popup`).pipe(
      catchError((err) => {
        this.patchPopup(true);
        console.error(err);
        return throwError(() => err);
      }),
    );
  }

  /** Called on logout so the next user in the same tab does not inherit this journey. */
  reset(): void {
    this._journey.set(null);
    this.error.set(null);
    this.isLoading.set(false);
  }

  /** No-op when the journey is not loaded — a logout mid-request must not resurrect it. */
  private patchPopup(showPopup: boolean): void {
    const current = this._journey();

    if (current) {
      this._journey.set({ ...current, showPopup });
    }
  }

  private patchStep(key: string, completed: boolean): void {
    const current = this._journey();

    if (!current) {
      return;
    }

    this._journey.set({
      ...current,
      steps: current.steps.map((step) =>
        step.key === key
          ? {
              ...step,
              completed,
              completedAt: completed ? new Date().toISOString() : null,
            }
          : step,
      ),
    });
  }
}

function countTrack(
  steps: JourneyStep[],
  track: JourneyTrack,
): JourneyProgress {
  const tracked = steps.filter((step) => step.track === track);

  return {
    completed: tracked.filter((step) => step.completed).length,
    total: tracked.length,
  };
}
